import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Edit, Delete, Restore } from "@mui/icons-material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const renderSectionStatus = (flgActivo) => {
  const actualStatus =
    flgActivo === 1 || flgActivo === "1" || flgActivo === undefined
      ? "Activo"
      : "Inactivo";
  const statusColors = {
    Activo: "success",
    Inactivo: "error",
  };
  return (
    <Chip
      label={actualStatus}
      color={statusColors[actualStatus] || "default"}
      size="small"
      sx={{ minWidth: "80px" }}
    />
  );
};

const StaticSectionCard = ({ item, handleModal, index }) => (
  <Grid item xs={12}>
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        opacity: 0.75,
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        },
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          "&:last-child": { pb: 2 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Box sx={{ flexGrow: 1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr auto", md: "1fr auto" },
                alignItems: "center",
                gap: 1,
                ml: 2,
              }}
            >
              <Typography
                variant="h6"
                fontWeight="medium"
                sx={{ wordBreak: "break-word" }}
              >
                {item.NOMBRE_SECCION}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                <Tooltip title="Restaurar sección">
                  <IconButton
                    onClick={() => handleModal("restore", true, item)}
                  >
                    <Restore color="success" fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
              {renderSectionStatus(item.FLG_ACTIVO)}
              <Typography variant="body2" color="text.secondary">
                Tipo: {item.NOMBRE_TIPO_SECCION || "Sin definir"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  </Grid>
);

const SortableSectionCard = ({ item, handleModal }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.ID_SECCION });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : "auto",
    position: isDragging ? "relative" : "static",
  };

  const stopPropagation = (e) => e.stopPropagation();

  return (
    <Grid item xs={12} ref={setNodeRef} style={style}>
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: isDragging
            ? "0 12px 32px rgba(0,0,0,0.22)"
            : "0 2px 8px rgba(0,0,0,0.08)",
          transition: "box-shadow 0.2s ease-in-out",
          opacity: isDragging ? 0.85 : 1,
          transform: isDragging ? "scale(1.02)" : "scale(1)",
          cursor: isDragging ? "grabbing" : "default",
          "&:hover": {
            boxShadow: isDragging
              ? "0 12px 32px rgba(0,0,0,0.22)"
              : "0 4px 12px rgba(0,0,0,0.12)",
          },
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            "&:last-child": { pb: 2 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Box
              {...attributes}
              {...listeners}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: isDragging ? "grabbing" : "grab",
                color: "text.disabled",
                px: 0.5,
                borderRadius: "6px",
                flexShrink: 0,
                "&:hover": { color: "primary.main", bgcolor: "action.hover" },
                touchAction: "none",
              }}
              aria-label="Arrastrar sección"
            >
              <DragIndicatorIcon fontSize="small" />
            </Box>

            <Box sx={{ flexGrow: 1 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr auto", md: "1fr auto" },
                  alignItems: "center",
                  gap: 1,
                  ml: 1,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="medium"
                  sx={{ wordBreak: "break-word" }}
                >
                  {item.NOMBRE_SECCION}
                </Typography>
                <Box
                  sx={{ display: "flex", gap: 1, flexShrink: 0 }}
                  onPointerDown={stopPropagation}
                >
                  <Tooltip title="Editar sección">
                    <IconButton onClick={() => handleModal("edit", true, item)}>
                      <Edit color="primary" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar sección">
                    <IconButton
                      onClick={() => handleModal("delete", true, item)}
                    >
                      <Delete color="error" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
                {renderSectionStatus(item.FLG_ACTIVO)}
                <Typography variant="body2" color="text.secondary">
                  Tipo: {item.NOMBRE_TIPO_SECCION || "Sin definir"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export const SectionCard = ({ item, handleModal, index }) => {
  const isActive = item.FLG_ACTIVO === 1 || item.FLG_ACTIVO === "1";

  if (isActive) {
    return <SortableSectionCard item={item} handleModal={handleModal} />;
  }

  return (
    <StaticSectionCard item={item} handleModal={handleModal} index={index} />
  );
};
